import { Box } from '@mui/material';
import '../Profile.css'
import { useEffect, useState } from "react";
import { callAPI } from 'utils/API';
import moment from 'moment';
import ButtonLarge1 from 'components/elements/ButtonLarge1';
import Confirmation from 'components/blocks/order/Confirmation';
import Headline5 from 'components/elements/Headline5';
import ContentNormal from 'components/elements/ContentNormal';
import CircularIndeterminate from 'components/blocks/progressBar/progressBar';
import KAMDashboard from '../KAMDashboard';

const OrdersPage = () => {
    const [confirmations, setConfirmations] = useState([])
    const [allOrders, setAllOrders] = useState([])
    const [isLoading, setIsLoading] = useState(true)
    const [freightInfo, setFreightInfo] = useState({})

    const fetchFreightIntervals = async () => {
        const response = await callAPI("/brand/freightIntervals");
        if (response && Object.keys(response).length) {
            setFreightInfo({
                enableDropshipFreightPrice: response.enableDropshipFreightPrice,
                enableFreightPriceInterval: response.enableFreightPriceInterval,
                freightIntervals: response.freightIntervals,
            });
        }
    };

    const fetchOrders = async () => {
        const userOrderLines = await callAPI(`/user/orders`);
        const confirmations = [];
        if (!userOrderLines || !userOrderLines.length) {
            setConfirmations([])
            setIsLoading(false)
            return
        }

        const confirmationIdToOrderlines = new Map()
        for (const orderline of userOrderLines) {
            if(!confirmationIdToOrderlines.get(orderline.ConfirmationID)){
                confirmationIdToOrderlines.set(orderline.ConfirmationID, [])
            }
            confirmationIdToOrderlines.get(orderline.ConfirmationID).push(orderline)
        }
        
        const confirmationIds = [...confirmationIdToOrderlines.keys()]
        for(const confirmationId of confirmationIds) {
            const orderlinesForConfirmation = confirmationIdToOrderlines.get(confirmationId)
            const firstOrderlineForConfirmation = orderlinesForConfirmation[0]
            
            const orderIdToOrderlines = new Map()
            for (const orderline of orderlinesForConfirmation) {
                if (!orderIdToOrderlines.get(orderline.OrderID)) {
                    orderIdToOrderlines.set(orderline.OrderID, [])
                }
                orderIdToOrderlines.get(orderline.OrderID).push(orderline)
            }

            const orders = []
            const orderIds = [...orderIdToOrderlines.keys()]
            for(const orderId of orderIds) {
                const variationIdToOrderlines = new Map()
                for (const orderline of orderIdToOrderlines.get(orderId)) {
                    if (!variationIdToOrderlines.get(orderline.VariationID)) {
                        variationIdToOrderlines.set(orderline.VariationID, [])
                    }
                    variationIdToOrderlines.get(orderline.VariationID).push(orderline)
                }

                const orderlinesByVariation = []
                const variationIds = [...variationIdToOrderlines.keys()]
                for(const variationId of variationIds) {
                    const orderlinesForVariation = variationIdToOrderlines.get(variationId)
                    const ol = orderlinesForVariation[0]
                    orderlinesByVariation.push({
                        OrderlineID: ol.OrderlineID,
                        VariationID: ol.VariationID,
                        ProductID: ol.ProductID,
                        imageUrl: ol.images[0]
                            ? ol.images[0].imageOriginSource
                            : "",
                        elementTitle: ol.elementTitle,
                        orderlineTitle: ol.orderlineTitle,
                        manufacturer: ol.manufacturer,
                        amountPoints: orderlinesForVariation.reduce((sum, l) => sum + l.orderAmountPoints, 0),
                        amountCurrency: orderlinesForVariation.reduce((sum, l) => sum + l.orderAmountCurrency, 0),
                        variationTitle: ol.variationTitle,
                        variationSize: ol.variationSize,
                        variationColor: ol.colorName,
                        translations: ol.translations,
                        quantity: orderlinesForVariation.length,
                    })
                }

                const orderOrderline = orderIdToOrderlines.get(orderId)[0]
                const translationKey = !!orderOrderline.orderFlowStatus ? `_ORDER_STATUS_${orderOrderline.orderFlowStatus}_` : null

                orders.push({
                    OrderID: orderOrderline.OrderID,
                    date: moment(orderOrderline.timeInsert),
                    amountPoints: orderIdToOrderlines.get(orderId).reduce((sum, ol) => sum + ol.orderAmountPoints, 0),
                    amountCurrency: orderIdToOrderlines.get(orderId).reduce((sum, ol) => sum + ol.orderAmountCurrency, 0),
                    orderFlowStatus: orderOrderline.orderFlowStatus || '-',
                    orderType: orderOrderline.orderType || '',
                    orderFlowStatusTranslationKey: translationKey || orderOrderline.orderFlowStatus,
                    orderRemark: orderOrderline.userOrderRemarks,
                    alertCount: orderOrderline.alertCount,
                    track: orderOrderline.orderGlsCustomerNumber,
                    orderConsigneeCustomerNumber: orderOrderline.orderConsigneeCustomerNumber || '',
                    orderLines: orderlinesByVariation,
                    trackingUrl: orderOrderline.trackingPrefix,
                    trackingNumber: orderOrderline.trackingNumber,
                    freightPoints: orderOrderline.freightPoints || 0,
                    freightPriceDKK: orderOrderline.freightPriceDKK || 0,
                    dropshipPoints: orderOrderline.dropshipPoints || 0,
                    dropshipPriceDKK: orderOrderline.dropshipPriceDKK || 0,
                    overrideShipmentDate: orderOrderline.overrideShipmentDate,
                    expectedShipmentDate: orderOrderline.expectedShipmentDate,
                    deliveryPaidWithCard: orderOrderline.deliveryPaidWithCard,
                    orderPaymentStatus: orderOrderline.orderPaymentStatus,
                })
            }

            const confirmation = {
                ConfirmationID: firstOrderlineForConfirmation.ConfirmationID,
                date: moment(firstOrderlineForConfirmation.timeInsert).format("DD/MM-YYYY"),
                amountPoints: orderlinesForConfirmation.reduce((sum, ol) => sum + ol.orderAmountPoints, 0),
                amountCurrency: orderlinesForConfirmation.reduce((sum, ol) => sum + ol.orderAmountCurrency, 0),
                freightPoints: orders.reduce((sum, o) => sum + o.freightPoints, 0),
                dropshipPoints: orders.reduce((sum, o) => sum + o.dropshipPoints, 0),
                totalFreightPoints: orders.reduce((sum, o) => sum + o.freightPoints + o.dropshipPoints, 0),
                totalFreightDKK: orders.reduce((sum, o) => sum + o.freightPriceDKK + o.dropshipPriceDKK, 0),
                orders: orders,
                deliveryPaidWithCard: orders.some(o => o.deliveryPaidWithCard),
                orderPaymentStatus: firstOrderlineForConfirmation.orderPaymentStatus,
            };

            
            if(firstOrderlineForConfirmation.customDeliveryDetails) {
                const deliveryDetails = firstOrderlineForConfirmation.customDeliveryDetails
                confirmation.orderConsignee = deliveryDetails.userName
                confirmation.orderConsigneeName = deliveryDetails.companyName
                confirmation.orderConsigneeAddress1 = deliveryDetails.address
                confirmation.orderConsigneeAddress2 = deliveryDetails.address2
                confirmation.orderConsigneeZipcode = deliveryDetails.zipcode
                confirmation.orderConsigneeCity = deliveryDetails.city
                confirmation.orderConsigneeCountry = deliveryDetails.country
                confirmation.orderConsigneePhone = `${deliveryDetails.phoneCountryCode ? `+${deliveryDetails.phoneCountryCode}` : ''}${deliveryDetails.phoneNumber}`
                confirmation.orderConsigneeEmail = deliveryDetails.emailAddress
                confirmation.shipmentReference = deliveryDetails.shipmentReference
            } else {
                confirmation.orderConsignee = firstOrderlineForConfirmation.orderConsignee
                confirmation.orderConsigneeName = firstOrderlineForConfirmation.orderConsigneeName
                confirmation.orderConsigneeAddress1 = firstOrderlineForConfirmation.orderConsigneeAddress1
                confirmation.orderConsigneeAddress2 = firstOrderlineForConfirmation.orderConsigneeAddress2
                confirmation.orderConsigneeZipcode = firstOrderlineForConfirmation.orderConsigneeZipcode
                confirmation.orderConsigneeCity = firstOrderlineForConfirmation.orderConsigneeCity
                confirmation.orderConsigneeCountry = firstOrderlineForConfirmation.orderConsigneeCountry
                confirmation.orderConsigneePhone = firstOrderlineForConfirmation.orderConsigneePhone
                confirmation.orderConsigneeEmail = firstOrderlineForConfirmation.orderConsigneeEmail
            }

            confirmations.push(confirmation)
            setIsLoading(false)
        }

        confirmations.sort((a, b) => b.ConfirmationID - a.ConfirmationID);
        setAllOrders(confirmations);
        return setConfirmations(confirmations.slice(0, 5));
    }

    useEffect(() => {
        fetchFreightIntervals()
        fetchOrders()
    }, [])

    return (
        <Box
            display={'flex'}
            flexDirection={'column'}
            flex={1}
            gap={10}
        >
            <KAMDashboard />
            {isLoading 
                ? 
                <Box display={'flex'} justifyContent={'center'} marginTop={2}>
                    <CircularIndeterminate />
                </Box>
                :
                !confirmations.length ?
                    <Box
                        display={'flex'}
                        flexDirection={'column'}
                        alignItems={'center'}
                        gap={1}
                        borderRadius={'14px'}
                        padding={4}
                    >
                        <Headline5
                            style={{textAlign: 'center'}}
                            translationKey="_PROFILE_PAGE_NO_ORDERS_CAPTION_"
                            fallbackText="Du har ingen ordrer endnu"
                        />
                        <ContentNormal
                            style={{textAlign: 'center'}}
                            translationKey="_PROFILE_PAGE_NO_ORDERS_TEXT_"
                            fallbackText="Når du laver din første ordre kan du følge den her. "
                        />
                    </Box>
                :
                    <Box display={'flex'} flexDirection={'column'} gap={4} >
                        {confirmations.map(confirmation => (
                            <Confirmation key={confirmation.ConfirmationID} confirmation={confirmation} refresh={fetchOrders} freightInfo={freightInfo} />
                        ))}

                        {!!confirmations.length && confirmations.length != allOrders.length && (
                            <ButtonLarge1
                                showIcon={false}
                                translationKey="_ORDERS_SEE_ALL_"
                                fallbackText="Se alle ordre"
                                style={{ maxWidth: 121, alignSelf: "center" }}
                                onClick={() => setConfirmations(allOrders)}
                            />
                        )}
                    </Box>
            }
        </Box>
    );
}

export default OrdersPage